import { ALERT_SEVERITIES } from "./alerting.js";
import { LOWER_UUID_PATTERN } from "./kernel/kernel-internal.js";
import { singleLineText } from "./single-line-text.js";
import { z } from "zod";
//#region src/telemetry-access.ts
/**
* DP telemetry access: who may read the metrics, logs and traces of one data plane through the CP
* broker, how an operator asks for that access, and the per-DP alert override (plan
* 2026-10-27-dp-telemetry-residency, WS-2; rulings R-005 to R-007, R-009, R-015, R-016, R-018,
* R-021, R-022, R-030 and R-042). The S4 contracts step (owner WS-14b) added the broker app
* metrics route of group F, the override routes of group E and the blank-reason refusal of group C
* (R-057 item 7, R-060, R-062 item 2, R-064).
*
* The module holds six groups:
*
* - A. The literals and the id grammar: {@link TELEMETRY_READ_PERMISSION},
*   {@link DP_TELEMETRY_READER_ROLE} and {@link telemetryAccessIdSchema}.
* - B. The in-process decision: {@link telemetryAccessDecisionSchema} and the port
*   {@link TelemetryAccessDecider}. No HTTP route serves a decision (R-015), and no decider keeps a
*   cache (R-016).
* - C. The seven access request routes of cp-auth-api (WS-13) and their schemas. A deny needs a
*   reason (R-042).
* - D. The coded refusal texts of those routes, {@link DP_ACCESS_REFUSALS}.
* - E. The per-DP alert override routes, body and answer of platform-api (WS-14, WS-14b, R-009).
* - F. The broker app metrics route of provisioning-svc (R-031, R-057 item 7).
*
* Callers: cp-auth-api (WS-5, WS-13), provisioning-svc (WS-12, WS-23), site-admin-api (WS-16),
* platform-api (WS-14, WS-14b), the console (WS-17) and `zion-ops` (WS-18).
*
* The module is pure: no clock, no I/O and no state beyond frozen constants. No schema has a
* `.transform()` or a `.default()`, so each schema validates and never rewrites, and
* `createOpenApiDocument()` renders each bound schema. It imports no Node builtin and reads no
* Node global, so the root barrel stays browser-safe.
*/
/**
* The one permission that opens the metrics, logs and traces of one data plane (R-007). A site
* holder (`platform_admin`, `fleet_operator`, R-005) reads every data plane. Any other operator
* reads one data plane through a live grant on `dp:<id>`, which an approved access request writes
* (R-018).
*/
const TELEMETRY_READ_PERMISSION = "cp:dp:telemetry-read";
/**
* The resource-scoped role that an approved access request grants on `dp:<id>` (R-018). It holds
* {@link TELEMETRY_READ_PERMISSION} alone. cp-auth-api (WS-5) seeds it; WS-13 writes its grant
* row and the console (WS-17) names it.
*/
const DP_TELEMETRY_READER_ROLE = "dp_telemetry_reader";
/**
* The grammar of every data plane, request, operator and grant id in this module: a lower-case
* 8-4-4-4-12 hex UUID of any version, as Postgres prints it. An upper-case UUID, a UUID in braces,
* a name such as `dp-app`, an empty string and an `svc:` or `machine:` subject each fail with a
* `ZodError` (F1, F3).
*/
const telemetryAccessIdSchema = z.string().regex(LOWER_UUID_PATTERN, "a lower-case UUID");
/**
* A UTC ISO 8601 timestamp: `YYYY-MM-DDTHH:MM:SS`, an optional fraction of any length, then an
* upper-case `Z`. An offset such as `+02:00`, a lower-case `t` or `z`, a time with no seconds, a
* date alone, hour 24, second 60 and an impossible day such as `2026-02-30` each fail. A fraction of
* nine digits passes, so a store keeps the full precision of an end.
*/
const utcInstantSchema = z.iso.datetime();
/**
* The fraction digits of a {@link utcInstantSchema} instant after the third, with the trailing
* zeros cut. `Date.parse` keeps only the first three digits.
*/
function fractionTail(instant) {
	return (/\.\d{3}(\d+)Z$/.exec(instant)?.[1] ?? "").replace(/0+$/, "");
}
/**
* Compare two {@link utcInstantSchema} instants at their full precision: negative when `a` is
* first, 0 when they are the same instant, positive when `a` is later. `Date.parse` keeps three
* fraction digits, so the digits after the third decide a tie; trailing zeros do not count.
*/
function compareUtcInstants(a, b) {
	const whole = Date.parse(a) - Date.parse(b);
	if (whole !== 0) return Math.sign(whole);
	const [tailA, tailB] = [fractionTail(a), fractionTail(b)];
	return Number(tailA > tailB) - Number(tailA < tailB);
}
/**
* True when the instant `end` is exactly `seconds` whole seconds after the instant `start`, at
* full precision. So `12:00:00.000123Z` plus 60 s is `12:01:00.000123Z` and not `12:01:00Z`.
*/
function isSecondsAfter(start, seconds, end) {
	return Date.parse(end) - Date.parse(start) === seconds * 1e3 && fractionTail(start) === fractionTail(end);
}
/**
* Why a decision denied a read:
*
* - `not_granted`: the operator holds the permission neither in its live site permissions nor in
*   a live grant on the data plane.
* - `scope_not_delegated`: the token is delegated (`scp` present) and its `scp` lacks
*   {@link TELEMETRY_READ_PERMISSION}. This wins over every site permission and grant.
*/
const TELEMETRY_ACCESS_DENY_REASONS = ["not_granted", "scope_not_delegated"];
/**
* The answer of one decision: may this operator read the telemetry of this data plane now.
*
* A discriminated union on `via`, each variant strict:
*
* - `site`, `allow: true`: the operator's live site permissions satisfy the permission (R-005,
*   R-030). It carries no grant key.
* - `grant`, `allow: true`: a live grant on `dp:<dataPlaneId>` satisfies it. `grantId` names the
*   grant; `grantExpiresAt` is its end as a UTC instant, or `null` for a grant with no end. The
*   broker caps a ticket at the lower of 15 minutes and `grantExpiresAt`.
* - `none`, `allow: false`: a deny, with one reason of {@link TELEMETRY_ACCESS_DENY_REASONS}.
*
* Every variant names `operatorId` (the verified principal's `sub`) and `dataPlaneId`, both
* {@link telemetryAccessIdSchema} ids. A mixed shape such as `allow: true, via: 'none'`, a site
* answer with a `grantId`, an offset time or an unknown reason fails with a `ZodError`. The answer
* makes no claim that the data plane exists, and it names no signal (R-007, R-008).
*/
const telemetryAccessDecisionSchema = z.discriminatedUnion("via", [
	z.object({
		allow: z.literal(true),
		via: z.literal("site"),
		operatorId: telemetryAccessIdSchema,
		dataPlaneId: telemetryAccessIdSchema
	}).strict(),
	z.object({
		allow: z.literal(true),
		via: z.literal("grant"),
		operatorId: telemetryAccessIdSchema,
		dataPlaneId: telemetryAccessIdSchema,
		grantId: telemetryAccessIdSchema,
		grantExpiresAt: utcInstantSchema.nullable()
	}).strict(),
	z.object({
		allow: z.literal(false),
		via: z.literal("none"),
		operatorId: telemetryAccessIdSchema,
		dataPlaneId: telemetryAccessIdSchema,
		reason: z.enum(TELEMETRY_ACCESS_DENY_REASONS)
	}).strict()
]);
/**
* The rejection of {@link TelemetryAccessDecider.decide} when the store that holds the live site
* permissions or the grants does not answer. The message is
* `telemetry access decision unavailable: <detail>`, and `name` is `TelemetryAccessUnavailable`.
* A caller treats it as a deny and does not retry inside the 30 s gateway clock (I17).
*
* The detail is a short fault name of 1 to 80 characters. It starts with a lower-case ASCII letter,
* and the other characters are lower-case ASCII letters, digits, spaces, `_`, `.` and `-`, for
* example `grant store down`. So the detail names no secret, no token, no connection string and no
* `key=value` pair. The constructor does not check the grammar;
* `describeTelemetryAccessDeciderContract` checks it on every rejection of a decider.
*/
var TelemetryAccessUnavailable = class extends Error {
	/** @param detail a short text that names the fault, for example `db down`. */
	constructor(detail) {
		super(`telemetry access decision unavailable: ${detail}`);
		this.name = "TelemetryAccessUnavailable";
	}
};
/** The prefix of the seven access request routes of cp-auth-api. The CP gateway sends `/v1/auth` there. */
const DP_ACCESS_REQUESTS_PATH = "/v1/auth/dp-access-requests";
/**
* The seven access request routes of cp-auth-api (WS-13 R1 to R7), each with its method and path
* template. One request is one row of `cp_auth.dp_access_requests`. Every route needs a verified
* first-party CP token; `NoDelegatedPrincipalGuard` refuses a delegated one with 403. A body,
* param or query off its schema answers 400 from the validation pipe.
*
* - `create` (R1): body {@link createDpAccessRequestSchema}; answer one
*   {@link dpAccessRequestViewSchema} row in status `pending`, 201.
* - `mine` (R2): no body; answer {@link dpAccessRequestListSchema}, the caller's 100 newest rows.
* - `withdraw` (R3): param {@link dpAccessRequestIdParamSchema}, body
*   {@link dpAccessRequestActionBodySchema}; the requester ends a `pending` row (`withdrawn`) or an
*   `approved` row (`revoked`). Another operator's row answers 404 `notFound`.
* - `list` (R4): query {@link listDpAccessRequestsQuerySchema}; answer
*   {@link dpAccessRequestListSchema}. Needs `cp:permission:grant` and the live approver.
* - `approve` (R5): param and empty action body. Needs `cp:permission:grant`, the live approver,
*   a caller other than the requester and the grantor ceiling (R-006).
* - `deny` (R6): param and body {@link dpAccessRequestDenyBodySchema}, which carries the required
*   reason (R-042). Needs `cp:permission:grant`, the live approver and a caller other than the
*   requester.
* - `revoke` (R7): param and empty action body. Needs `cp:permission:grant` and the live approver.
*   Any live approver may revoke any approval, its own decision included.
*
* The live approver (R-006, R-064 item 1): cp-auth-api reads the caller's operator row in the
* database, in the transaction of the route. The caller must be an active operator that holds
* `platform_admin` now, directly or through a group, and that role must hold
* `cp:permission:grant`. A site role in the token claims does not count. The check runs before
* any read of the request, so every request id, absent or in any status, answers the same bare
* 403 to a caller that fails it. `list`, `approve`, `deny` and `revoke` run it; `create`, `mine`
* and `withdraw` do not. The three approver POSTs answer 201 with the view.
*
* No route reads one row by id (`GET /:id`).
*/
const DP_ACCESS_REQUEST_ROUTES = {
	create: {
		method: "POST",
		path: "/v1/auth/dp-access-requests"
	},
	mine: {
		method: "GET",
		path: "/v1/auth/dp-access-requests/mine"
	},
	withdraw: {
		method: "POST",
		path: "/v1/auth/dp-access-requests/:id/withdraw"
	},
	list: {
		method: "GET",
		path: "/v1/auth/dp-access-requests"
	},
	approve: {
		method: "POST",
		path: "/v1/auth/dp-access-requests/:id/approve"
	},
	deny: {
		method: "POST",
		path: "/v1/auth/dp-access-requests/:id/deny"
	},
	revoke: {
		method: "POST",
		path: "/v1/auth/dp-access-requests/:id/revoke"
	}
};
/**
* The concrete path of `route`.
*
* Input: `route`, a key of {@link DP_ACCESS_REQUEST_ROUTES}; `id`, the request id. The four verb
* routes (`withdraw`, `approve`, `deny`, `revoke`) need an `id` that passes
* {@link telemetryAccessIdSchema}, which replaces `:id`. The other three (`create`, `mine`,
* `list`) take no `id`.
*
* Output: the path, for example `/v1/auth/dp-access-requests/<id>/approve`.
*
* Errors: throws `TypeError` that names the route for a verb route with no id or a bad id, and for
* an `id` given to a route that takes none. Pure.
*/
function dpAccessRequestPath(route, id) {
	const template = DP_ACCESS_REQUEST_ROUTES[route].path;
	if (!template.includes(":id")) {
		if (id !== void 0) throw new TypeError(`dpAccessRequestPath: the route ${route} takes no id`);
		return template;
	}
	if (id === void 0 || !LOWER_UUID_PATTERN.test(id)) throw new TypeError(`dpAccessRequestPath: the route ${route} needs a lower-case UUID id`);
	return template.replace(":id", id);
}
/**
* The bounds of the access duration, in seconds (R-006): at least 900 (the 15 minute broker
* ticket), 14,400 (4 h) when the request names none, at most 86,400 (24 h). Frozen.
*/
const DP_ACCESS_DURATION_SECONDS = Object.freeze({
	min: 900,
	default: 14400,
	max: 86400
});
/** A `pending` request reads `expired` this many seconds after its `createdAt` (R-006): 24 h. */
const DP_ACCESS_PENDING_TTL_SECONDS = 86400;
/** The longest request reason and deny reason, in UTF-16 code units. */
const DP_ACCESS_REASON_MAX_LENGTH = 500;
/** The row count of the `list` route when the query names no `limit`. cp-auth-api applies it. */
const DP_ACCESS_LIST_DEFAULT = 50;
/** The highest `limit` of the `list` route, and the longest list answer. */
const DP_ACCESS_LIST_MAX = 200;
/**
* The seven statuses of a request, in this order:
*
* - `pending`: asked, not decided, not past `pendingExpiresAt`.
* - `approved`: decided yes; the grant is live until `accessEndsAt`.
* - `denied`: decided no, with a reason (R-042).
* - `expired`: still pending at `pendingExpiresAt`.
* - `withdrawn`: the requester ended a pending request.
* - `revoked`: an approved request ended before `accessEndsAt`, by the requester or an approver.
* - `ended`: an approved request at or past `accessEndsAt`.
*/
const DP_ACCESS_REQUEST_STATUSES = [
	"pending",
	"approved",
	"denied",
	"expired",
	"withdrawn",
	"revoked",
	"ended"
];
/**
* One request or deny reason: 1 to 500 UTF-16 code units on one line, with no control character
* and at least one character that is not white space.
*
* A reason of white space alone fails with the message `must not be blank` (R-064 item 2). The
* white space set is the set of `String.prototype.trim` and of the regular expression class `\s`:
* the space, U+00A0, U+FEFF, every `Zs` character, U+2028 and U+2029. So this schema refuses
* exactly the blank reasons that `storedDpAccessReason` of cp-auth-api refuses with a 400. A
* reason with a zero-width space (U+200B) and no other character is not blank under either rule.
*
* A reason with a line separator (U+2028) or a paragraph separator (U+2029) among other text fails
* with the message `must be one line of text`, as `storedDpAccessReason` and the 0070 CHECKs refuse
* it. A reason of white space alone that holds one fails with `must not be blank` only.
*
* Each rule is a pattern, so `z.toJSONSchema` renders it as `pattern` and never throws.
*/
const dpAccessReasonSchema = singleLineText(500).regex(/^(?:[^\u2028\u2029]*|\s*)$/u, "must be one line of text").regex(/\S/, "must not be blank");
/** One access duration: an integer from 900 to 86,400 seconds. A numeric string fails. */
const dpAccessDurationSchema = z.number().int().min(DP_ACCESS_DURATION_SECONDS.min).max(DP_ACCESS_DURATION_SECONDS.max);
/**
* The body of `create` (R1). Strict.
*
* - `dpId`: the data plane, a {@link telemetryAccessIdSchema} id. No check that it exists.
* - `reason`: why the operator needs access, one line of 1 to 500 code units that is not blank.
* - `durationSeconds`: optional, an integer from 900 to 86,400. An absent key stays absent, and
*   cp-auth-api stores 14,400.
*
* The body has no requester, approver, grant or status key, so an extra key fails with 400, as do
* a non-integer, a numeric string and a value out of range.
*/
const createDpAccessRequestSchema = z.object({
	dpId: telemetryAccessIdSchema,
	reason: dpAccessReasonSchema,
	durationSeconds: dpAccessDurationSchema.optional()
}).strict();
/** The `:id` param of the four verb routes: a {@link telemetryAccessIdSchema} id. Strict. */
const dpAccessRequestIdParamSchema = z.object({ id: telemetryAccessIdSchema }).strict();
/**
* The body of `withdraw`, `approve` and `revoke`: the empty object. Strict, so an approver cannot
* change the duration or the data plane (F1).
*/
const dpAccessRequestActionBodySchema = z.object({}).strict();
/**
* The body of `deny` (R6): the one key `reason`, required (R-042). One line of 1 to 500 code units
* with no control character. Strict: an absent, empty or blank reason and any other key fail with
* 400.
* cp-auth-api stores it and the view returns it as `denyReason`.
*/
const dpAccessRequestDenyBodySchema = z.object({ reason: dpAccessReasonSchema }).strict();
/**
* The query of `list` (R4). Strict; every key optional.
*
* - `status`: filters on the effective status, one of {@link DP_ACCESS_REQUEST_STATUSES}.
* - `dpId`: filters on one data plane.
* - `limit`: an integer from 1 to 200, coerced from the query text. Absent, cp-auth-api returns
*   {@link DP_ACCESS_LIST_DEFAULT} rows.
*/
const listDpAccessRequestsQuerySchema = z.object({
	status: z.enum(DP_ACCESS_REQUEST_STATUSES).optional(),
	dpId: telemetryAccessIdSchema.optional(),
	limit: z.coerce.number().int().min(1).max(200).optional()
}).strict();
/** The 9 keys that every view variant shares, whatever its status; `status` and the 7 decision keys complete it. */
const dpAccessRequestViewBase = {
	id: telemetryAccessIdSchema,
	requesterId: telemetryAccessIdSchema,
	requesterName: z.string(),
	requesterEmail: z.string().min(1).max(254),
	dpId: telemetryAccessIdSchema,
	reason: dpAccessReasonSchema,
	durationSeconds: dpAccessDurationSchema,
	createdAt: utcInstantSchema,
	pendingExpiresAt: utcInstantSchema
};
const setId = telemetryAccessIdSchema;
const setInstant = utcInstantSchema;
const nil = z.null();
/**
* One request as every route answers it: a discriminated union on `status`, each variant strict.
* Every variant has the same 17 keys, always present: `id`, `requesterId`, `requesterName`,
* `requesterEmail` (1 to 254 characters), `dpId`, `reason`, `durationSeconds`, `status`,
* `createdAt`, `pendingExpiresAt`, `decidedBy`, `decidedAt`, `grantId`, `accessEndsAt`, `endedBy`,
* `endedAt` and `denyReason`. Times are UTC ISO 8601; an offset fails.
*
* Which keys hold a value, per status (else `null`):
*
* | status | `decidedBy`, `decidedAt` | `grantId`, `accessEndsAt` | `endedBy` | `endedAt` | `denyReason` |
* |---|---|---|---|---|---|
* | `pending` | null | null | null | null | null |
* | `expired` | null | null | null | null or set | null |
* | `denied` | set | null | null | null | set |
* | `withdrawn` | null | null | set | set | null |
* | `approved` | set | set | null | null | null |
* | `ended` | set | set | null | null or set | null |
* | `revoked` | set | set | set | set | null |
*
* `status` is the effective status at read time: a stored `pending` row past `pendingExpiresAt`
* reads `expired`, and a stored `approved` row at or past `accessEndsAt` reads `ended`, each with
* `endedAt` null until the sweeper stores it. `accessEndsAt` equals the grant's `expires_at`.
* `reason`, `denyReason` and `requesterName` are untrusted text for a reader. `requesterName` has
* no length cap. A blank `reason` or `denyReason` fails, because both request bodies refuse one.
*
* Five rules across keys, each a `ZodError` when broken:
*
* - `decidedBy` never equals `requesterId`: no operator approves or denies its own request (R-006).
* - A `withdrawn` row has `endedBy` equal to `requesterId`: only the requester withdraws.
* - A `revoked` row has `endedAt` strictly before `accessEndsAt`, at full precision: a row at or
*   past `accessEndsAt` reads `ended`, so a revoke there cannot happen.
* - Every row has `pendingExpiresAt` exactly {@link DP_ACCESS_PENDING_TTL_SECONDS} after
*   `createdAt`, at full precision: cp-auth-api sets both from one clock read.
* - An `approved`, `revoked` or `ended` row has `accessEndsAt` exactly `durationSeconds` after
*   `decidedAt`, at full precision: the approval sets both from one clock read. So no view grants
*   more or less time than the request asked for.
*/
const dpAccessRequestViewSchema = z.discriminatedUnion("status", [
	z.object({
		...dpAccessRequestViewBase,
		status: z.literal("pending"),
		decidedBy: nil,
		decidedAt: nil,
		grantId: nil,
		accessEndsAt: nil,
		endedBy: nil,
		endedAt: nil,
		denyReason: nil
	}).strict(),
	z.object({
		...dpAccessRequestViewBase,
		status: z.literal("approved"),
		decidedBy: setId,
		decidedAt: setInstant,
		grantId: setId,
		accessEndsAt: setInstant,
		endedBy: nil,
		endedAt: nil,
		denyReason: nil
	}).strict(),
	z.object({
		...dpAccessRequestViewBase,
		status: z.literal("denied"),
		decidedBy: setId,
		decidedAt: setInstant,
		grantId: nil,
		accessEndsAt: nil,
		endedBy: nil,
		endedAt: nil,
		denyReason: dpAccessReasonSchema
	}).strict(),
	z.object({
		...dpAccessRequestViewBase,
		status: z.literal("expired"),
		decidedBy: nil,
		decidedAt: nil,
		grantId: nil,
		accessEndsAt: nil,
		endedBy: nil,
		endedAt: setInstant.nullable(),
		denyReason: nil
	}).strict(),
	z.object({
		...dpAccessRequestViewBase,
		status: z.literal("withdrawn"),
		decidedBy: nil,
		decidedAt: nil,
		grantId: nil,
		accessEndsAt: nil,
		endedBy: setId,
		endedAt: setInstant,
		denyReason: nil
	}).strict(),
	z.object({
		...dpAccessRequestViewBase,
		status: z.literal("revoked"),
		decidedBy: setId,
		decidedAt: setInstant,
		grantId: setId,
		accessEndsAt: setInstant,
		endedBy: setId,
		endedAt: setInstant,
		denyReason: nil
	}).strict(),
	z.object({
		...dpAccessRequestViewBase,
		status: z.literal("ended"),
		decidedBy: setId,
		decidedAt: setInstant,
		grantId: setId,
		accessEndsAt: setInstant,
		endedBy: nil,
		endedAt: setInstant.nullable(),
		denyReason: nil
	}).strict()
]).superRefine((view, ctx) => {
	if (view.decidedBy !== null && view.decidedBy === view.requesterId) ctx.addIssue({
		code: "custom",
		path: ["decidedBy"],
		message: "the requester cannot decide its own request"
	});
	if (view.status === "withdrawn" && view.endedBy !== view.requesterId) ctx.addIssue({
		code: "custom",
		path: ["endedBy"],
		message: "only the requester withdraws a request"
	});
	if (view.status === "revoked" && compareUtcInstants(view.endedAt, view.accessEndsAt) >= 0) ctx.addIssue({
		code: "custom",
		path: ["endedAt"],
		message: "a revoke ends before accessEndsAt"
	});
	if (!isSecondsAfter(view.createdAt, 86400, view.pendingExpiresAt)) ctx.addIssue({
		code: "custom",
		path: ["pendingExpiresAt"],
		message: "pendingExpiresAt is createdAt plus 86400 s"
	});
	if (view.accessEndsAt !== null && !isSecondsAfter(view.decidedAt, view.durationSeconds, view.accessEndsAt)) ctx.addIssue({
		code: "custom",
		path: ["accessEndsAt"],
		message: "accessEndsAt is decidedAt plus durationSeconds"
	});
});
/**
* The answer of `mine` and `list`: a bare array of at most 200 views, newest `createdAt` first.
* `list` answers `limit` rows at most; `mine` 100. An object such as `{ items: [] }` fails, and so
* does a row whose `createdAt` is later than the `createdAt` of the row before it, at full
* precision. Two rows with the same `createdAt` keep either order.
*/
const dpAccessRequestListSchema = z.array(dpAccessRequestViewSchema).max(200).superRefine((views, ctx) => {
	let previous = null;
	for (const [index, view] of views.entries()) {
		if (previous !== null && compareUtcInstants(previous, view.createdAt) < 0) ctx.addIssue({
			code: "custom",
			path: [index, "createdAt"],
			message: "the list is newest createdAt first"
		});
		previous = view.createdAt;
	}
});
/**
* The coded refusals of the access request routes, each with its HTTP status and its message.
* cp-auth-api throws each with the NestJS exception envelope `{ statusCode, message, error }`:
*
* - `notFound` (404): no request has the id, or a withdraw names another operator's request.
* - `notPending` (409): approve or deny of a request whose effective status is not `pending`.
* - `notOpen` (409): withdraw of a request that is neither `pending` nor `approved`.
* - `notApproved` (409): revoke of a request whose effective status is not `approved`.
* - `alreadyPending` (409): create while the requester has a `pending` request on the data plane.
* - `alreadyGranted` (409): create while the requester holds a live grant on the data plane.
* - `queueFull` (409): create while the pending queue is at its cap.
* - `tooManyOpen` (409): create while the requester has too many open requests.
* - `requesterInactive` (409): approve of a request whose requester is no longer active.
*
* A bare 403 (self-decision, no live `platform_admin` row, the grantor ceiling, an `svc:`
* subject), the 403 of `NoDelegatedPrincipalGuard` and the pipe 400 carry no text of this map. A
* client keys its copy on the status and shows a 409 `message` as untrusted text. Frozen.
*/
const DP_ACCESS_REFUSALS = Object.freeze({
	notFound: {
		status: 404,
		message: "dp access request not found"
	},
	notPending: {
		status: 409,
		message: "dp access request is not pending"
	},
	notOpen: {
		status: 409,
		message: "dp access request is not open"
	},
	notApproved: {
		status: 409,
		message: "dp access request is not approved"
	},
	alreadyPending: {
		status: 409,
		message: "dp access request already pending"
	},
	alreadyGranted: {
		status: 409,
		message: "dp access already granted"
	},
	queueFull: {
		status: 409,
		message: "dp access request queue is full"
	},
	tooManyOpen: {
		status: 409,
		message: "too many open dp access requests"
	},
	requesterInactive: {
		status: 409,
		message: "requester is not active"
	}
});
/**
* The bounds of an alert window, in seconds: 60 (two 30 s DP scrapes) to 86,400 (24 h, four times
* the widest `[6h]` range of the CP rule corpus). Frozen.
*/
const ALERT_WINDOW_SECONDS = Object.freeze({
	min: 60,
	max: 86400
});
/** The bounds of an alert `for:` duration, in seconds: 0 to 86,400 (24 h). Frozen. */
const ALERT_FOR_SECONDS = Object.freeze({
	min: 0,
	max: 86400
});
/**
* The three alert rule override routes of platform-api (WS-14, WS-14b, R-009, R-060 item 2, R-062
* item 2), each with its method and path template. The console (WS-17) and the site-admin-api
* proxy (WS-16) send them under `/v1/admin`, which the CP gateway routes to site-admin-api.
* `:ruleName` is the code name of a rule, such as `DpKernelVersionHeld`, and never a row id.
* Every route needs a verified CP token (`CpJwtAuthGuard`, 401).
*
* - `upsert`: PUT. Query `dpId`, optional: absent for the CP-wide override, else the data plane.
*   Body {@link alertRuleOverrideBodySchema}; answer 200 with one {@link alertRuleOverrideSchema}
*   row. Needs `cp:settings:write`. Audit action `alerting.rule_upserted`.
* - `delete`: DELETE. The same query, and no body. It deletes the one override row of the rule in
*   that scope and answers 204 with an empty body. Needs `cp:settings:write`. Audit action
*   `alerting.rule_override_deleted`, one row per deleted override. The data plane then follows
*   the CP-wide override, or the code default when no CP-wide override exists.
* - `list`: GET. No query; answer 200 with an array of {@link alertRuleOverrideSchema} rows,
*   ordered by `ruleName`, then by `dataPlaneId` with the CP-wide row first. Needs
*   `cp:platform:read`.
*
* The refusals of `upsert` and `delete`, in this order: 401 with no verified token; 403 without
* `cp:settings:write`; 400 for a query or a body off its strict schema, such as an unknown query
* key or a `dpId` that is no UUID; 404 for a rule name that names no rule; 404 for a `dpId` with
* no data plane placement. `upsert` then answers 400 or 409 per its own checks. `delete` answers
* 404 when no override row exists in the scope, and writes no audit row then. A `delete` of the
* CP-wide override deletes no per-DP row.
*/
const ALERT_RULE_OVERRIDE_ROUTES = {
	upsert: {
		method: "PUT",
		path: "/v1/admin/alerting/rules/:ruleName"
	},
	delete: {
		method: "DELETE",
		path: "/v1/admin/alerting/rules/:ruleName"
	},
	list: {
		method: "GET",
		path: "/v1/admin/alerting/rules/overrides"
	}
};
/**
* The body of `PUT /v1/admin/alerting/rules/:ruleName?dpId=` on platform-api (WS-14, R-009). It
* replaces platform-api's local `upsertAlertRuleSchema`. Strict; every key required, so a PUT
* states the whole override:
*
* - `thresholdValue`: a finite number. NaN and both infinities fail, because the value reaches
*   PromQL text.
* - `severity`: one of `ALERT_SEVERITIES`.
* - `enabled`: a boolean.
* - `windowSeconds`: the range of the expression, an integer from 60 to 86,400, or `null` to keep
*   the code default.
* - `forSeconds`: the rule's `for:`, an integer from 0 to 86,400, or `null` to keep the code default.
*
* The scope is the `dpId` query, so a `dataPlaneId` or `ruleName` body key fails with 400.
*/
const alertRuleOverrideBodySchema = z.object({
	thresholdValue: z.number(),
	severity: z.enum(ALERT_SEVERITIES),
	enabled: z.boolean(),
	windowSeconds: z.number().int().min(ALERT_WINDOW_SECONDS.min).max(ALERT_WINDOW_SECONDS.max).nullable(),
	forSeconds: z.number().int().min(ALERT_FOR_SECONDS.min).max(ALERT_FOR_SECONDS.max).nullable()
}).strict();
/**
* The 200 answer of the override PUT: the body plus five keys of the stored row. Strict; every key
* required.
*
* - `id`: the row id, a UUID.
* - `ruleName`: the rule, a non-empty string.
* - `dataPlaneId`: the data plane, a {@link telemetryAccessIdSchema} id, or `null` for the
*   CP-wide override. So any id that the decider takes as a data plane passes here too.
* - `updatedBy`: the operator, a {@link telemetryAccessIdSchema} id. The nil UUID, which
*   platform-api stores for a service subject, passes.
* - `updatedAt`: a UTC instant.
*/
const alertRuleOverrideSchema = alertRuleOverrideBodySchema.extend({
	id: z.uuid(),
	ruleName: z.string().min(1),
	dataPlaneId: telemetryAccessIdSchema.nullable(),
	updatedBy: telemetryAccessIdSchema,
	updatedAt: utcInstantSchema
}).strict();
/**
* The route template of the broker app metrics read of provisioning-svc (R-031, R-057 item 7), in
* the NestJS form with no leading `/`: `v1/telemetry/apps/:appId/dp/:dpId/metrics`. The value is
* the one that provisioning-svc served before the S4 contracts step, so a move of the controller
* to this constant (WS-23) changes no route.
*
* Who reads it: `TelemetryAppMetricsController` of provisioning-svc binds `@Get` to it, and
* site-admin-api (WS-16) builds its call with {@link telemetryAppMetricsPath}. The route reads the
* fixed-template metrics of one app on one data plane. `:appId` and `:dpId` are lower-case UUIDs,
* and the query is `AppMetricsRouteQuerySchema` of `@zion/sdk-cp-dp-contracts` without `slug`,
* because the broker binds the slug from the app id. The route needs `cp:app:runtime:read` on the
* app (R-022, R-031), not {@link TELEMETRY_READ_PERMISSION}.
*
* The broker store routes sit under `/v1/telemetry/dp/:dpId/`, so this path starts with
* `v1/telemetry/apps/`: a path under the store prefix would match a store route first.
*/
const TELEMETRY_APP_METRICS_ROUTE = "v1/telemetry/apps/:appId/dp/:dpId/metrics";
/**
* The concrete path of the broker app metrics read, with a leading `/`, for an HTTP client such as
* site-admin-api (WS-16).
*
* Input: `appId` and `dpId`, the caller's ids. Each must pass {@link telemetryAccessIdSchema}: a
* lower-case 8-4-4-4-12 hex UUID.
*
* Output: `/v1/telemetry/apps/<appId>/dp/<dpId>/metrics`, the template of
* {@link TELEMETRY_APP_METRICS_ROUTE} with the two ids in place. The caller appends the query.
*
* Errors: throws `TypeError` that names the bad parameter when `appId` or `dpId` fails the id
* grammar, `appId` first. So a slug, an upper-case UUID or a text with `/`, `?`, `#` or `..`
* never reaches the path (F3). Pure.
*/
function telemetryAppMetricsPath(appId, dpId) {
	if (!LOWER_UUID_PATTERN.test(appId)) throw new TypeError("telemetryAppMetricsPath: appId must be a lower-case UUID");
	if (!LOWER_UUID_PATTERN.test(dpId)) throw new TypeError("telemetryAppMetricsPath: dpId must be a lower-case UUID");
	return `/${TELEMETRY_APP_METRICS_ROUTE.replace(":appId", appId).replace(":dpId", dpId)}`;
}
//#endregion
export { ALERT_FOR_SECONDS, ALERT_RULE_OVERRIDE_ROUTES, ALERT_WINDOW_SECONDS, DP_ACCESS_DURATION_SECONDS, DP_ACCESS_LIST_DEFAULT, DP_ACCESS_LIST_MAX, DP_ACCESS_PENDING_TTL_SECONDS, DP_ACCESS_REASON_MAX_LENGTH, DP_ACCESS_REFUSALS, DP_ACCESS_REQUESTS_PATH, DP_ACCESS_REQUEST_ROUTES, DP_ACCESS_REQUEST_STATUSES, DP_TELEMETRY_READER_ROLE, TELEMETRY_ACCESS_DENY_REASONS, TELEMETRY_APP_METRICS_ROUTE, TELEMETRY_READ_PERMISSION, TelemetryAccessUnavailable, alertRuleOverrideBodySchema, alertRuleOverrideSchema, createDpAccessRequestSchema, dpAccessRequestActionBodySchema, dpAccessRequestDenyBodySchema, dpAccessRequestIdParamSchema, dpAccessRequestListSchema, dpAccessRequestPath, dpAccessRequestViewSchema, listDpAccessRequestsQuerySchema, telemetryAccessDecisionSchema, telemetryAccessIdSchema, telemetryAppMetricsPath };

//# sourceMappingURL=telemetry-access.js.map